import React from 'react';
import { Grid, Modal, Tag, Typography } from 'antd';
import {
  BarcodeOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MailOutlined,
  MobileOutlined,
  PhoneOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import QuickActionDetails from './QuickActionDetails';
import { getConditionStyles } from './quickActionUtils';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const DetailTile = ({ icon, label, value, wrap = false }) => (
  <div style={{
    minWidth: 0,
    padding: '3px 0',
  }}>
    <Text type="secondary" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
      {icon} {label}
    </Text>
    <Text strong ellipsis={wrap ? undefined : { tooltip: value || '-' }} style={{ display: 'block', fontSize: '12px', marginTop: '2px', overflowWrap: wrap ? 'anywhere' : undefined, whiteSpace: wrap ? 'normal' : undefined }}>
      {value || '-'}
    </Text>
  </div>
);

const QuickActionModalTitle = ({ app }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  if (!app) return null;

  const conditionStyles = getConditionStyles(app.condition);

  return (
    <div style={{ paddingRight: isMobile ? '28px' : '24px', width: '100%' }}>
      <div style={{ alignItems: isMobile ? 'flex-start' : 'center', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px', justifyContent: 'space-between' }}>
        <Title level={isMobile ? 4 : 3} style={{ lineHeight: 1.25, margin: 0, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <MobileOutlined style={{ color: '#1890ff', marginRight: '8px' }} />{app.deviceModel}
        </Title>
        <div style={{
          alignItems: 'center',
          display: 'flex',
          flexShrink: 0,
          gap: '8px',
        }}>
          <Text style={{ color: conditionStyles.labelColor, fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
            Device Condition
          </Text>
          <Tag style={{ background: conditionStyles.background, border: conditionStyles.border, color: conditionStyles.textColor, fontWeight: 700, margin: 0 }}>
            {app.condition || '-'}
          </Tag>
        </div>
      </div>
      <div style={{
        display: 'grid',
        gap: '8px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
        marginTop: '12px',
        borderTop: '1px solid #f0f0f0',
        paddingTop: '10px',
      }}>
        <DetailTile icon={<IdcardOutlined />} label="Application ID" value={app.id} />
        <DetailTile icon={<UserOutlined />} label="Customer" value={app.customerName} />
        <DetailTile icon={<CalendarOutlined />} label="Created" value={app.date} />
        <DetailTile icon={<BarcodeOutlined />} label="IMEI" value={app.imeiNumber} />
      </div>
      <div style={{
        alignItems: 'center',
        background: '#f7faff',
        borderLeft: '3px solid #1890ff',
        borderRadius: '0 8px 8px 0',
        display: 'grid',
        gap: '6px 20px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        marginTop: '10px',
        padding: '10px 14px',
      }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Text strong style={{ color: '#262626', fontSize: '13px' }}>
            <ShopOutlined style={{ color: '#1890ff', marginRight: '7px' }} />
            {app.storeName || 'Store Details'}
          </Text>
        </div>
        <DetailTile icon={<PhoneOutlined />} label="Mobile" value={app.storeMobile} />
        <DetailTile icon={<MailOutlined />} label="Email" value={app.storeEmail} />
        <DetailTile icon={<EnvironmentOutlined />} label="Address" value={app.storeAddress} wrap />
      </div>
    </div>
  );
};

const QuickActionModal = ({
  app,
  open,
  loadingApp,
  onClose,
  onRejectRequest,
  onDirectAccept,
  bidView = 'new',
  now,
  onUpdateBid,
  onBidUpdated,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  return (
    <Modal
      title={<QuickActionModalTitle app={app} />}
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '96vw' : '90%'}
      style={{ top: isMobile ? 12 : 40, maxWidth: '1200px' }}
      styles={{
        content: { padding: isMobile ? '16px 12px' : '20px 24px' },
        body: {
          maxHeight: isMobile ? 'calc(100vh - 190px)' : 'calc(100vh - 220px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: '#f8fafc',
        },
      }}
    >
      {app && (
        <QuickActionDetails
          app={app}
          loadingApp={loadingApp}
          onRejectRequest={onRejectRequest}
          onDirectAccept={onDirectAccept}
          bidView={bidView}
          now={now}
          onUpdateBid={onUpdateBid}
          onBidUpdated={onBidUpdated}
        />
      )}
    </Modal>
  );
};

export default QuickActionModal;
